import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Conversations, WebSocketMessageType } from "../model";
import { fetchConversationByUuid, WS_API_CONVERSATION_URL } from "../../api/ConversationApi";
import { getAuthToken } from "../security/AuthContext";
import './ConversationPage.scss';
import Badge from "../list/Badge";

const ConversationPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [conversation, setConversation] = useState<Conversations | null>(null);
  const [topic, setTopic] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false); // To trigger animation

  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const loadConversation = async () => {
      try {
        const data = await fetchConversationByUuid(uuid!!);
        setConversation(data);
        setIsLoaded(true);
        setTopic(data.topic);
      } catch (err) {
        setError('Failed to load conversation');
      }
    };

    loadConversation();
  }, [uuid]);

  useEffect(() => {
    if (uuid) {
      const token = getAuthToken();
      const ws = new WebSocket(`${WS_API_CONVERSATION_URL}/${uuid}?token=${token}`);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        socketRef.current = ws;
      };

      ws.onmessage = (event) => {
        const messageData = JSON.parse(event.data);

        if (messageData.type === WebSocketMessageType.MESSAGE) {
          const message = messageData.message;
          setConversation(prev => prev ? {
            ...prev,
            chatMessages: [...prev.chatMessages || [], message]
          } : prev);

          // Trigger animation when a new message is received
          setIsNewMessage(true);
          setTimeout(() => setIsNewMessage(false), 1000);  // Reset animation state after animation duration
        } else if (messageData.type === WebSocketMessageType.CONVERSATION) {
          const summary = messageData.message?.summary;
          setConversation(prev => prev ? {
            ...prev,
            summary: summary
          } : prev);

          // Trigger animation for the metadata panel
          setIsNewMessage(true);
          setTimeout(() => setIsNewMessage(false), 1000);  // Reset animation state
        }
      };

      ws.onerror = (error) => {
        setError('WebSocket error');
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        socketRef.current = null;
      };

      // Cleanup function
      return () => {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      };
    }
  }, [uuid]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation?.chatMessages]);

  const handleSendMessage = () => {
    const ws = socketRef.current;
    if (newMessage && ws?.readyState === WebSocket.OPEN) {
      ws.send(newMessage);
      setNewMessage('');
    }
  };

  const formatSummary = (text: string) => {
    return text.replace(/\*\*(.*?)\*\*/g, '<b>\n$1</b>'); // Replace all **text** with <b>text</b>
  };

  const handleInputChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    setNewMessage(event.target.value);
  };

  return (
    <div className="conversation-page">
      <div>
        <Badge badgeTitle={isConnected ? 'CONNECTED' : 'DISCONNECTED'} />

        <div className="conversation-layout">
          <div className={`chat-container ${isNewMessage ? 'new-message' : ''}`}>
            <div className="chat-messages" ref={chatContainerRef}>
              {conversation?.chatMessages?.map((message) => (
                <div
                  key={message.uuid}
                  className={`message ${message.senderType === 'HUMAN' ? 'right' : 'left'} ${message.senderType === 'AGENT' ? 'agent' : ''}`}
                >
                  <div className="message-body">{message.body}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={`metadata-panel ${isNewMessage ? 'new-message' : ''}`}>
            <h3>Metadata</h3>
            <p>Channel type: {conversation?.type}</p>
            {conversation?.summary && (
              <div className="metadata-summary">
                <div className="metadata-summary__ai-text"
                     dangerouslySetInnerHTML={{__html: formatSummary(conversation.summary)}}
                />
              </div>
            )}
          </div>
        </div>

        <div className="message-input">
          <textarea
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Type your message..."
            className="message-input__textarea-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <button
            className="message-input__send-button"
            onClick={handleSendMessage}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationPage;